"use client";

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import RichContent from "../ui/RichContent";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
}

interface FAQSectionProps {
  className?: string;
  title?: string;
  faqData?: FAQItem[];
}

const FAQSection: React.FC<FAQSectionProps> = ({
  className = "",
  title = "FAQ",
  faqData: propsFaqData,
}) => {
  const [openItems, setOpenItems] = useState<number[]>([]);

  const faqData = propsFaqData;

  const toggleItem = (id: number) => {
    setOpenItems((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const sectionVariants = {
    hidden: {
      opacity: 0,
      y: 60,
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.8,
        ease: "circOut" as const,
      },
    },
  };

  return (
    <motion.section
      className={`relative ${className}`}
      style={{ backgroundColor: "#F4F5F6" }}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-100px" }}
      variants={sectionVariants}
      id="faq"
    >
      {/* Background Pattern */}
      {/* <div className="absolute inset-0 overflow-hidden opacity-30 mix-blend-luminosity pointer-events-none">
        <svg
          width="1318"
          height="788"
          viewBox="0 0 1318 788"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className="w-full h-full object-cover"
          style={{
            position: "absolute",
            right: "-1570px",
            bottom: "-357.556px",
          }}
        >
          <g
            style={{ mixBlendMode: "luminosity" }}
            opacity="0.3"
            clipPath="url(#clip0_3777_991)"
          >
            <path
              d="M2887.5 -137.555H0.5V1145.56H2887.5V-137.555Z"
              fill="#F0F0F0"
            />
            <mask
              id="mask0_3777_991"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="230"
              width="847"
              height="916"
            >
              <path
                d="M455.801 723.169L0.5 230.99V1145.55H846.538L536.944 810.886L455.801 723.169Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask0_3777_991)">
              <path
                d="M846.538 230.99H0.5V1145.55H846.538V230.99Z"
                fill="url(#paint0_linear_3777_991)"
              />
            </g>
            <mask
              id="mask1_3777_991"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="118"
              y="-138"
              width="862"
              height="949"
            >
              <path
                d="M118.055 -137.557C118.055 -137.557 359.066 500.132 455.796 723.167L536.939 810.885L979.193 -137.557H118.055Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask1_3777_991)">
              <path
                d="M979.201 -137.557H118.062V810.885H979.201V-137.557Z"
                fill="url(#paint1_linear_3777_991)"
              />
            </g>
            <mask
              id="mask2_3777_991"
              style={{ maskType: "luminance" }}
              maskUnits="userSpaceOnUse"
              x="0"
              y="-138"
              width="511"
              height="921"
            >
              <path
                d="M0.5 230.991L510.915 782.748L270.026 -137.555H0.5V230.991Z"
                fill="white"
              />
            </mask>
            <g mask="url(#mask2_3777_991)">
              <path
                d="M510.915 -137.555H0.5V782.748H510.915V-137.555Z"
                fill="url(#paint2_linear_3777_991)"
              />
            </g>
          </g>
          <defs>
            <linearGradient
              id="paint0_linear_3777_991"
              x1="364.156"
              y1="1155.08"
              x2="461.062"
              y2="393.049"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E8E8E8" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
            <linearGradient
              id="paint1_linear_3777_991"
              x1="180.198"
              y1="583.488"
              x2="1080.98"
              y2="-19.9697"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E8E8E8" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
            <linearGradient
              id="paint2_linear_3777_991"
              x1="158.838"
              y1="779.376"
              x2="316.97"
              y2="33.7171"
              gradientUnits="userSpaceOnUse"
            >
              <stop stopColor="#E8E8E8" />
              <stop offset="1" stopColor="white" />
            </linearGradient>
            <clipPath id="clip0_3777_991">
              <rect
                width="2887"
                height="1283.11"
                fill="white"
                transform="translate(0.5 -137.556)"
              />
            </clipPath>
          </defs>
        </svg>
      </div> */}

      <div className="relative z-10 group">
        <div>
          {/* Section Header */}
          <div className="flex flex-col items-center gap-4 mb-12 lg:mb-16">
            <h2 className="text-[#0A1119] text-center font-inter text-3xl sm:text-4xl lg:text-[40px] font-medium leading-[58px] tracking-[-1.44px]">
              {title}
            </h2>
            <div className="w-[50px] h-[2px] bg-[#1A5852]"></div>
          </div>

          {/* FAQ Container */}
          <div className="w-full">
            <div className="bg-white rounded-lg">
              {/* FAQ Items Container */}
              <div className="p-6 sm:p-8 lg:p-12">
                <div className="flex flex-col gap-4">
                  {faqData.map((item, index) => (
                    <div
                      key={item.id}
                      className="bg-white rounded-2xl p-6 sm:p-8 group/accordion transition-colors"
                      style={{ boxShadow: "0 30px 60px 0 rgba(0, 0, 0, 0.08)" }}
                    >
                      <div className="flex flex-col">
                        {/* FAQ Item Header - Clickable */}
                        <button
                          onClick={() => toggleItem(item.id)}
                          className="flex justify-between items-start gap-4 w-full text-left cursor-pointer"
                        >
                          {/* question */}
                          <div className="flex gap-3 flex-1">
                            <span className="text-[#1A6B62] font-inter text-xl sm:text-2xl font-medium leading-[34px] tracking-[-0.72px] flex-shrink-0">
                              Q.
                            </span>
                            <h3 className="text-[#1A6B62] font-inter text-lg sm:text-xl lg:text-2xl font-medium leading-[34px] tracking-[-0.72px]">
                              {item.question}
                            </h3>
                          </div>

                          {/* Toggle Icon */}
                          <div
                            className={`relative flex w-8 h-8 p-1 flex-col justify-center items-center rounded-full border transition-colors ${
                              openItems.includes(item.id)
                                ? "bg-[#1A5852] border-[#1A5852]"
                                : "border-[#C9CDD3]"
                            } group-hover/accordion:bg-[#1A5852] group-hover/accordion:border-[#1A5852] flex-shrink-0`}
                          >
                            {/* Horizontal line (always visible) */}
                            <div
                              className={`absolute w-4 h-[2px] rounded-3xl ${
                                openItems.includes(item.id)
                                  ? "bg-white"
                                  : "bg-[#1A5852]"
                              } group-hover/accordion:bg-white`}
                            />
                            {/* Vertical line (rotates on toggle) */}
                            <div
                              className={`absolute w-[2px] h-4 rounded-3xl origin-center transition-transform duration-300 ${
                                openItems.includes(item.id)
                                  ? "bg-white rotate-90"
                                  : "bg-[#1A5852]"
                              } group-hover/accordion:bg-white`}
                            />
                          </div>
                        </button>

                        {/* FAQ Item answer */}
                        <AnimatePresence>
                          {openItems.includes(item.id) && item.answer && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3, ease: "easeInOut" }}
                              className="overflow-hidden"
                            >
                              <div className="flex mt-6 gap-3">
                                <span className="text-[#1A6B62] font-inter text-xl sm:text-2xl font-medium leading-[34px] tracking-[-0.72px] flex-shrink-0">
                                  A.
                                </span>
                                <RichContent
                                  html={item.answer}
                                  className="text-[#3D434C] font-inter text-base leading-7 mt-[-14px]"
                                />
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Progress Bar */}
        <div className="flex h-[2px] bg-gray-25 relative">
          <div className="absolute left-0 top-0 h-[2px] bg-[#1C8579] w-0 group-hover:w-full transition-all duration-500"></div>
        </div>
      </div>
    </motion.section>
  );
};

export default FAQSection;
